import React, {ReactElement, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import FormGroup from '../common/form/FormGroup'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import TextInput from '../common/form/TextInput'
import {
    Control,
    FieldArray,
    FieldArrayMethodProps,
    FieldArrayWithId,
    useFieldArray,
    UseFieldArrayMove,
    useForm,
    useFormState
} from 'react-hook-form'
import {getAutocompleteSchedules, regexForNumberWithoutEmergencyNr} from '../common/util/helpers'
import {useSelector} from 'react-redux'
import {selectedSiteSelector} from '../authentication/redux'
import {ForwardSchedule, PhoneNumber, Site, TimeBasedForwarding} from '../app/types'
import * as yup from 'yup'
import {object} from 'yup'
import {ForwardToNumberField} from "../common/components/ForwardToNumberField";
import {yupResolver} from "@hookform/resolvers/yup";
import FormContainer from "../common/form/FormContainer";
import {useFormSubmission} from "../common/servercall/useFormSubmission";
import {FormHelperText, Grid, IconButton, Paper} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorAlert from "../common/components/ErrorAlert";
import Typography from "@mui/material/Typography";
import {ErrorMessage} from "@hookform/error-message";
import {UseFieldArrayRemove} from "react-hook-form/dist/types/fieldArray";
import {SortableContext, useSortable} from "@dnd-kit/sortable";
import {CSS} from '@dnd-kit/utilities'
import {DndContext, MouseSensor, TouchSensor, useSensor, useSensors} from '@dnd-kit/core'
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers'
import {AddButton} from "../common/components/AddButton";


type Props = {
    timeBasedForwarding: TimeBasedForwarding
    fullWidth?: boolean
    phoneNumbersToForwardTo: PhoneNumber[]
    onSubmit: (formData: TimeBasedForwarding) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
    allowMultipleForwards: boolean
}

export const schema = (maxForwardingSchedules: number) => yup.object().shape({
    forwardingSchedules: yup.array().of(object().shape({
        scheduleUuid: yup.string().trim().nullable(),
        forwardingNumber: yup.string().when(['scheduleUuid'], {
            is: (schedule: string) => !!schedule,
            then: schema => yup.string().trim().max(22, 'too-long').required('required-m').matches(regexForNumberWithoutEmergencyNr, {
            message: 'invalid-phone-number',
            excludeEmptyString: true,
        }).nullable(),
            otherwise: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
            message: 'invalid-phone-number',
            excludeEmptyString: true,
        }).nullable(),
    }),
    })).max(maxForwardingSchedules, 'max-20-schedules-allowed' ),
    openingHoursSchedule: yup.object().transform((value: ForwardSchedule ) => !value.scheduleUuid ? null : value).shape({
        forwardingNumber: yup
            .string()
            .when(['scheduleUuid'], {
                is: (schedule: string) => !!schedule,
                then: schema => yup.string().trim().max(22, 'too-long').required('required-m').matches(regexForNumberWithoutEmergencyNr, {
                    message: 'invalid-phone-number',
                    excludeEmptyString: true,
                }).nullable(),
                otherwise: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                    message: 'invalid-phone-number',
                    excludeEmptyString: true,
                }).nullable(),
            }),
    }).nullable(true)
})

export const TimeBasedForwardingForm = ({ timeBasedForwarding, fullWidth, phoneNumbersToForwardTo, onSubmit, buildFormButtons, allowMultipleForwards}: Props) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)

    const { control, handleSubmit, setError} = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema( 20)),
        defaultValues: {...timeBasedForwarding,
            forwardingSchedules: timeBasedForwarding.forwardingSchedules == null || timeBasedForwarding.forwardingSchedules.length === 0 ?
                [
                    {scheduleUuid : '', scheduleName: '', forwardingNumber: ''}
                ] : timeBasedForwarding.forwardingSchedules,
        }
    })


    const { submit, onError, isSubmitting, serverError } = useFormSubmission<TimeBasedForwarding>(onSubmit, setError)

    const openingHoursOptions = useMemo(() => {
        return getAutocompleteSchedules(t, 'opening-hours', site, timeBasedForwarding?.openingHoursSchedule || null)
    }, [site, t, timeBasedForwarding])

    const { fields, remove, move, insert, replace } = useFieldArray({
        control,
        name: `forwardingSchedules`,
    })

    const { errors } = useFormState({
        control,
    })

    const forwardScheduleErrors = errors['forwardingSchedules']
    const isInvalid = !!(errors && forwardScheduleErrors && forwardScheduleErrors.type === 'max' )

    const nightServiceOptions = useMemo(() =>
        ['OFF', 'ON', 'USE_GROUP'].map(mode => ({
            value: `${mode}`,
            label: `${t(`flows.edit.night-service-modes.${mode}`)}`,
        })), [t])


    // @ts-ignore
    return (
        <>
            <ErrorAlert showAlert={!!serverError} />
            <FormContainer onSubmit={handleSubmit(submit, onError)} >

                <FormGroup fullWidth={fullWidth || false} >
                    {
                        !!timeBasedForwarding?.nightServiceMode ? <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    readonly={false}
                                    label={t('flows.edit.night-service')}
                                    name={'nightServiceMode'}
                                    control={control}
                                    required={false}
                                    options={nightServiceOptions}
                                />
                            </FormFieldBox>
                        </FormRow> : <></>
                    }

                    <FormRow>
                        <FormFieldBox>
                            <Typography variant={'subtitle1'} >
                                <strong>{t('time-based-forwarding.forwarding-schedule')}</strong>
                            </Typography>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <ForwardingForm
                                        phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                        control={control}
                                        site={site} timeBasedForwarding={timeBasedForwarding}
                                        fields={fields}
                                        insert={insert}
                                        remove={remove}
                                        move={move}
                                        replace={replace}
                                        allowMultipleForwards={allowMultipleForwards}
                        />
                    </FormRow>
                    {isInvalid && (
                        <FormRow>
                            <FormFieldBox>
                                <ErrorMessage
                                    errors={errors}
                                    // @ts-ignore
                                    name={'forwardingSchedules'}
                                    render={({ message }) => <FormHelperText
                                        error={true}>{t(`general.form-error.${message}`)}</FormHelperText>}
                                />
                            </FormFieldBox>
                        </FormRow>
                    )}
                    <FormRow>
                        <FormFieldBox>
                            <Typography variant={'subtitle1'} >
                                <strong>{t('time-based-forwarding.openinghours-schedule')}</strong>
                            </Typography>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput
                                readonly={false}
                                label={t('schedules.schedule')}
                                name={'openingHoursSchedule.scheduleUuid'}
                                control={control}
                                required={false}
                                autoCompleteOptions={openingHoursOptions}
                            />
                        </FormFieldBox>
                        <FormFieldBox>
                            <ForwardToNumberField phoneNumbersToForwardTo={phoneNumbersToForwardTo} defaultNumber={timeBasedForwarding?.openingHoursSchedule?.forwardingNumber || ''} readonly={false} required={false} label={t('time-based-forwarding.openinghours-number')} name={'openingHoursSchedule.forwardingNumber'} control={control} />
                        </FormFieldBox>
                    </FormRow>
                </FormGroup>
                {buildFormButtons(isSubmitting)}
            </FormContainer>
        </>
    )
}

const ForwardForm = ({
                          index,
                          control,
                          phoneNumbersToForwardTo,
                          site,
                          fields
                      }: {
    index: number
    control: Control<any>
    phoneNumbersToForwardTo: PhoneNumber[]
    site: Site | null
    fields: FieldArrayWithId<TimeBasedForwarding, "forwardingSchedules", "id">[]
}) => {
    const { t } = useTranslation()

    const holidayOptions = useMemo(() => {
        return getAutocompleteSchedules(t, 'holidays', site, !fields[index].createdInForm ? fields[index] : null )
    }, [site, t, index, fields])

    return (<FormRow>
                <FormFieldBox>
                    <TextInput
                        readonly={false}
                        label={t('schedules.schedule')}
                        name={`forwardingSchedules[${index}].scheduleUuid`}
                        control={control}
                        required={true}
                        autoCompleteOptions={holidayOptions}
                    />
                </FormFieldBox>
                <FormFieldBox>
                    <ForwardToNumberField phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                          defaultNumber={!fields[index].createdInForm ? fields[index].forwardingNumber : '' }
                                          readonly={false} required={true}
                                          label={t('time-based-forwarding.forwarding-number')} name={`forwardingSchedules[${index}].forwardingNumber`} control={control} />
                </FormFieldBox>
            </FormRow>
    )
}

const ForwardingForm = ({
                            allowMultipleForwards,
                            control,
                            timeBasedForwarding,
                            phoneNumbersToForwardTo,
                            site, fields, replace, insert, remove, move
                        }: {
    allowMultipleForwards: boolean
    control: Control<any>
    timeBasedForwarding: TimeBasedForwarding
    phoneNumbersToForwardTo: PhoneNumber[]
    site: Site | null,
    fields: FieldArrayWithId<TimeBasedForwarding, "forwardingSchedules", "id">[]
    insert: (index: number, value: (FieldArray<TimeBasedForwarding, "forwardingSchedules"> | FieldArray<TimeBasedForwarding, "forwardingSchedules">[]), options?: FieldArrayMethodProps) => void
    remove: UseFieldArrayRemove
    move: UseFieldArrayMove
    replace: (value: (FieldArray<TimeBasedForwarding, "forwardingSchedules"> | FieldArray<TimeBasedForwarding, "forwardingSchedules">[])) => void
}) => {

    const onClickDelete = useCallback((index: number) =>
            () => {
                fields.length === 1 ? replace([{
                    scheduleUuid: '',
                    scheduleName: '',
                    forwardingNumber: '',
                    createdInForm: true
                }]) : remove(index)
            }
        , [fields, replace, remove])

    const modifiers = [restrictToVerticalAxis]

    const { t } = useTranslation()

    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);

    const sensors = useSensors(
        mouseSensor,
        touchSensor,
    );

    return <DndContext
                sensors={sensors}
                modifiers={modifiers}
                onDragEnd={(event) => {
                    const { active, over } = event
                    if (over && active.id !== over?.id) {
                        const activeIndex = active.data.current?.sortable?.index
                        const overIndex = over.data.current?.sortable?.index
                        if (activeIndex !== undefined && overIndex !== undefined) {
                            move(activeIndex, overIndex)
                        }
                    }
                }}
            >
                {allowMultipleForwards &&
                    <Grid container xs={12} alignItems={'center'} justifyContent={'center'} flexWrap={'nowrap'}>
                        <AddButton
                            size={'medium'}
                            label={t('time-based-forwarding.add-schedule')}
                            onClick={() =>
                                insert(0, {
                                    scheduleUuid: '',
                                    scheduleName: '',
                                    forwardingNumber: '',
                                    createdInForm: true,
                                })
                            }
                        />
                    </Grid>
                }
                <SortableContext items={fields}>
                    { fields.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <SortableItem id={item.id}>
                                {({ attributes, listeners }) => (
                                    <>
                                        {allowMultipleForwards
                                            && <Grid container item xs={1} justifyContent={'end'} alignSelf={'center'}>
                                                {fields.length !== 1 && (
                                                    <IconButton title={t('time-based-forwarding.drag-to-change-order')} style={{cursor: "grab"}} {...attributes} {...listeners}>
                                                        <SwapVertIcon  style={{cursor: "grab"}} fontSize={"large"}/>
                                                    </IconButton>
                                                )}
                                            </Grid>}
                                        {allowMultipleForwards ?
                                            <Grid item xs={allowMultipleForwards? 10 : 12}>
                                                <Paper sx={{ p: 3 }}>
                                                    <ForwardForm
                                                        index={index}
                                                        fields={fields}
                                                        phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                                        control={control}
                                                        site={site}/>
                                                </Paper>
                                            </Grid> : <ForwardForm
                                                index={index}
                                                fields={fields}
                                                phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                                control={control}
                                                site={site}/>
                                        }
                                        {allowMultipleForwards &&
                                            <Grid item container xs={1} alignItems={'center'} justifyContent={'center'} flexWrap={'nowrap'} ml={1}>
                                                {fields.length !== 1 && (
                                                    <IconButton
                                                        aria-label='delete'
                                                        component='div'
                                                        onClick={onClickDelete(index)}
                                                    >
                                                        <DeleteIcon fontSize='inherit'/>
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        }
                                    </>
                                )}
                            </SortableItem>
                        </React.Fragment>
                    ))}
                </SortableContext>
    </DndContext>
}

type UseSortableReturn = Omit<
    ReturnType<typeof useSortable>,
    'setNodeRef' | 'transform' | 'transition'
>

function SortableItem(props: {
    id: string
    children: (args: UseSortableReturn) => React.ReactNode
}) {
    const { setNodeRef, transform, transition, ...rest } = useSortable({
        id: props.id
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    }

    useEffect(() => {
        if (rest.isDragging) {
            document.body.style.cursor = 'grabbing';
        } else {
            document.body.removeAttribute('style');
        }
    }, [rest.isDragging]);

    return (
        <Grid container item ref={setNodeRef} style={style} flexWrap={'nowrap'} >
            {props.children({ ...rest })}
        </Grid>
    )
}
